import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, RefreshCw, AlertCircle, Clock, Users, BarChart2 } from 'lucide-react'
import { adminApi, type AdminStats, type AdminTicketListItem } from '../services/api'
import { PriorityBadge } from '../components/PriorityBadge'
import { StatusBadge } from '../components/StatusBadge'

// ─── Filter state ─────────────────────────────────────────────────────────────

interface Filters {
  status: string
  category: string
  priority: string
  search: string
}

const DEFAULT_FILTERS: Filters = { status: '', category: '', priority: '', search: '' }

const STATUS_OPTIONS = ['', 'open', 'awaiting_response', 'in_progress', 'resolved', 'closed']
const CATEGORY_OPTIONS = ['', 'bug', 'feature', 'how-to', 'billing', 'security', 'other']
const PRIORITY_OPTIONS = ['', 'low', 'medium', 'high', 'critical']

// ─── Stat card ────────────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  icon: Icon,
  highlight,
}: {
  label: string
  value: string | number
  icon: React.ElementType
  highlight?: boolean
}) {
  return (
    <div
      className={`bg-white rounded-xl border p-4 flex items-center gap-4 ${
        highlight ? 'border-brand-400 ring-1 ring-brand-200' : 'border-gray-200'
      }`}
    >
      <div
        className={`p-2.5 rounded-lg ${highlight ? 'bg-brand-50 text-brand-600' : 'bg-gray-50 text-gray-500'}`}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-xs text-gray-500 mt-0.5">{label}</p>
      </div>
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export function AdminDashboard() {
  const navigate = useNavigate()
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [tickets, setTickets] = useState<AdminTicketListItem[]>([])
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS)
  const [pendingSearch, setPendingSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (f: Filters) => {
    setLoading(true)
    setError(null)
    try {
      const [statsData, ticketData] = await Promise.all([
        adminApi.getStats(),
        adminApi.listTickets({
          status: f.status || undefined,
          category: f.category || undefined,
          priority: f.priority || undefined,
          search: f.search || undefined,
          limit: 100,
        }),
      ])
      setStats(statsData)
      setTickets(ticketData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tickets')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load(filters)
  }, [load, filters])

  const applySearch = () => {
    setFilters((f) => ({ ...f, search: pendingSearch }))
  }

  const setFilter = (key: keyof Filters, value: string) => {
    setFilters((f) => ({ ...f, [key]: value }))
  }

  const fmtTime = (iso: string) => {
    const d = new Date(iso)
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  }

  const fmtHours = (h: number | null) =>
    h == null ? '—' : h < 1 ? `${Math.round(h * 60)}m` : `${h.toFixed(1)}h`

  return (
    <div>
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Support Queue</h1>
          <p className="text-sm text-gray-500 mt-0.5">LlamAssist staff view — all customer tickets</p>
        </div>
        <button
          onClick={() => load(filters)}
          disabled={loading}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-40"
          title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Stats row */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <StatCard label="Total tickets" value={stats.total_tickets} icon={BarChart2} />
          <StatCard label="Open" value={stats.open_tickets} icon={AlertCircle} highlight={stats.open_tickets > 0} />
          <StatCard label="Unassigned" value={stats.unassigned_tickets} icon={Users} highlight={stats.unassigned_tickets > 0} />
          <StatCard label="Avg response" value={fmtHours(stats.avg_response_time_hours)} icon={Clock} />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex flex-wrap gap-3">
        {/* Search */}
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-gray-400 shrink-0" />
          <input
            type="text"
            value={pendingSearch}
            onChange={(e) => setPendingSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applySearch()}
            placeholder="Search subject, email…"
            className="flex-1 text-sm outline-none"
          />
          {pendingSearch !== filters.search && (
            <button
              onClick={applySearch}
              className="text-xs text-brand-600 font-medium hover:text-brand-700"
            >
              Apply
            </button>
          )}
        </div>

        {/* Status filter */}
        <select
          value={filters.status}
          onChange={(e) => setFilter('status', e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white outline-none focus:ring-2 focus:ring-brand-500"
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.filter(Boolean).map((s) => (
            <option key={s} value={s}>
              {s.replace('_', ' ')}
            </option>
          ))}
        </select>

        {/* Category filter */}
        <select
          value={filters.category}
          onChange={(e) => setFilter('category', e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white outline-none focus:ring-2 focus:ring-brand-500"
        >
          <option value="">All categories</option>
          {CATEGORY_OPTIONS.filter(Boolean).map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>

        {/* Priority filter */}
        <select
          value={filters.priority}
          onChange={(e) => setFilter('priority', e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white outline-none focus:ring-2 focus:ring-brand-500"
        >
          <option value="">All priorities</option>
          {PRIORITY_OPTIONS.filter(Boolean).map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>

        {/* Clear filters */}
        {(filters.status || filters.category || filters.priority || filters.search) && (
          <button
            onClick={() => {
              setFilters(DEFAULT_FILTERS)
              setPendingSearch('')
            }}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Clear
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">
          {error}
        </div>
      )}

      {/* Ticket table */}
      {loading && tickets.length === 0 ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-20 text-gray-500">
          <p className="text-sm">No tickets match the current filters.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Table header */}
          <div className="grid grid-cols-[1fr_140px_90px_90px_140px] gap-3 px-4 py-2.5 border-b border-gray-100 bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wide">
            <span>Ticket</span>
            <span>Customer</span>
            <span>Status</span>
            <span>Priority</span>
            <span>Created</span>
          </div>

          {/* Rows */}
          {tickets.map((t) => (
            <button
              key={t.id}
              onClick={() => navigate(`/admin/tickets/${t.id}`)}
              className="w-full grid grid-cols-[1fr_140px_90px_90px_140px] gap-3 px-4 py-3.5 border-b border-gray-50 hover:bg-gray-50 transition-colors text-left last:border-b-0"
            >
              <div className="min-w-0">
                <span className="text-xs text-gray-400 font-mono mr-2">{t.ticket_number}</span>
                <span className="text-sm font-medium text-gray-900 truncate">{t.subject}</span>
              </div>
              <span className="text-xs text-gray-500 truncate self-center">
                {t.user_email ?? '—'}
              </span>
              <span className="self-center">
                <StatusBadge status={t.status} />
              </span>
              <span className="self-center">
                <PriorityBadge priority={t.priority} />
              </span>
              <span className="text-xs text-gray-400 self-center">{fmtTime(t.created_at)}</span>
            </button>
          ))}
        </div>
      )}

      <p className="text-xs text-gray-400 mt-3 text-right">{tickets.length} ticket{tickets.length !== 1 ? 's' : ''} shown</p>
    </div>
  )
}
